import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { getContacts, getContact, getContactTimeline, recomputeContactScore, type WebContact } from '@/services'
import { AppButton, EmptyState, Badge, ScoreChip, IconButton, Skeleton, TableSkeleton, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { relTime, formatDateTime } from '@/lib/utils'

function utmSource(c: WebContact): string {
  return String((c as any).first_utm_source || c.utm_source || '—')
}

function ContactDetailSkeleton() {
  return (
    <>
      <div className="page-header">
        <div className="page-title-block" style={{ minWidth: 0, flex: 1 }}>
          <Skeleton width={80} height={14} style={{ marginBottom: 10 }} />
          <Skeleton width={280} height={32} />
          <Skeleton width={360} height={14} style={{ marginTop: 10 }} />
        </div>
        <div className="page-actions" style={{ gap: 8 }}>
          <Skeleton width={94} height={34} radius={8} />
          <Skeleton width={34} height={34} radius={8} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Skeleton width={86} height={26} radius={999} />
        <Skeleton width={110} height={26} radius={999} />
        <Skeleton width={120} height={26} radius={999} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card"><div className="card-header"><h3>Identidad</h3></div><div className="card-body"><Skeleton height={190} /></div></div>
        <div className="card"><div className="card-header"><h3>Actividad</h3></div><div className="card-body"><Skeleton height={190} /></div></div>
      </div>
    </>
  )
}

// ---------- LIST ----------
export function ContactsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [identified, setIdentified] = useState<'all' | 'yes' | 'no'>('all')
  const [converted, setConverted] = useState<'all' | 'yes' | 'no'>('all')
  const [sort, setSort] = useState<'score' | 'last_seen'>('score')

  const { data: result, isLoading } = useQuery({
    queryKey: ['contacts', identified, converted, sort, search],
    queryFn: () => getContacts({
      isIdentified: identified === 'all' ? undefined : identified === 'yes',
      converted: converted === 'all' ? undefined : converted === 'yes',
      sort,
      search: search || undefined,
      pageSize: 100,
    }),
  })
  const contacts = result?.data || []
  const total = result?.count || contacts.length
  const fireCount = contacts.filter((c) => (c.score || 0) >= 81).length
  const hotCount = contacts.filter((c) => (c.score || 0) >= 51 && (c.score || 0) <= 80).length

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Marketing</div>
          <h1>Contactos</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={320} height={14} /> : <>{total} contactos · <strong style={{ color: 'var(--score-fire-accent)' }}>{fireCount} 🔥 en llamas</strong> · <strong style={{ color: 'var(--score-hot-accent)' }}>{hotCount} calientes</strong> — llamá hoy</>}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar CSV</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar" style={{ gap: 12 }}>
          <div className="table-search">
            <Icon name="search" size={14} />
            <input placeholder="Buscar por email…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="metric-toggle">
            {([['all', 'Todos'], ['yes', 'Identificados'], ['no', 'Anónimos']] as const).map(([k, lbl]) => (
              <button key={k} className={identified === k ? 'active' : ''} onClick={() => setIdentified(k)} type="button">{lbl}</button>
            ))}
          </div>
          <div className="metric-toggle">
            {([['all', 'Todos'], ['yes', 'Convirtió'], ['no', 'No convirtió']] as const).map(([k, lbl]) => (
              <button key={k} className={converted === k ? 'active' : ''} onClick={() => setConverted(k)} type="button">{lbl}</button>
            ))}
          </div>
          <div className="metric-toggle" style={{ marginLeft: 'auto' }}>
            {([['score', 'Por score'], ['last_seen', 'Recientes']] as const).map(([k, lbl]) => (
              <button key={k} className={sort === k ? 'active' : ''} onClick={() => setSort(k)} type="button">{lbl}</button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton columns={6} rows={8} />
        ) : contacts.length === 0 ? (
          <EmptyState icon="fingerprint" title="Sin contactos" desc="Todavía no hay contactos con ese filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr><th>Contacto</th><th style={{ width: 120 }}>Score</th><th>Origen</th><th>Convirtió</th><th>Última visita</th><th /></tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)} {...rowActivation(() => navigate(`/contacts/${c.id}`))}>
                  <td>
                    <div className="cell-primary">{c.is_identified ? (c.email || c.name) : 'Visitante anónimo'}</div>
                    <div className="cell-secondary"><span className="code-chip">#{c.id.slice(0, 8)}</span></div>
                  </td>
                  <td><ScoreChip score={c.score || 0} /></td>
                  <td><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{utmSource(c)}</span></td>
                  <td>{c.converted ? <Badge tone="success" size="sm">Sí</Badge> : <Badge tone="neutral" size="sm">No</Badge>}</td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.last_seen ? relTime(c.last_seen) : '—'}</span></td>
                  <td style={{ textAlign: 'right' }}><Icon name="chevronRight" size={14} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

// ---------- DETAIL ----------
export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const { data: contact, isLoading } = useQuery({ queryKey: ['contact', id], queryFn: () => getContact(id!), enabled: !!id })
  const { data: timeline } = useQuery({ queryKey: ['contact-timeline', id], queryFn: () => getContactTimeline(id!, 100), enabled: !!id })

  const recomputeMutation = useMutation({
    mutationFn: () => recomputeContactScore(id!),
    onSuccess: () => { toast.success('Score recalculado'); qc.invalidateQueries({ queryKey: ['contact', id] }) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo recalcular'),
  })

  if (isLoading || !contact) {
    return <ContactDetailSkeleton />
  }

  const events = Array.isArray((timeline as any)?.data) ? (timeline as any).data : Array.isArray(timeline) ? timeline : []

  return (
    <>
      <div className="page-header">
        <div className="page-title-block" style={{ minWidth: 0, flex: 1 }}>
          <div className="eyebrow"><Link to="/contacts" style={{ color: 'var(--text-3)', textDecoration: 'none', fontWeight: 600 }}>← Contactos</Link></div>
          <h1 style={{ marginTop: 6 }}>{contact.is_identified ? (contact.email || contact.name) : 'Visitante anónimo'}</h1>
          <div className="subtitle" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="code-chip">#{contact.id.slice(0, 8)}</span>
            <span>·</span>
            <span>origen {utmSource(contact)}</span>
          </div>
        </div>
        <div className="page-actions" style={{ gap: 8 }}>
          {contact.is_identified
            ? <AppButton variant="success" icon="send">Contactar</AppButton>
            : <AppButton variant="secondary" icon="fingerprint" disabled>Anónimo</AppButton>}
          <IconButton icon="refresh" tooltip="Recalcular score" onClick={() => recomputeMutation.mutate()} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <ScoreChip score={contact.score || 0} />
        {contact.is_identified ? <Badge tone="success" dot>Identificado</Badge> : <Badge tone="neutral" dot>Anónimo</Badge>}
        {contact.converted ? <Badge tone="success"><Icon name="check" size={10} />Convirtió</Badge> : <Badge tone="outline">Sin conversión</Badge>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Identidad</h3></div>
          <div className="card-body">
            <dl className="dl" style={{ gridTemplateColumns: '120px 1fr' }}>
              <dt>Email</dt><dd>{contact.email || '—'}</dd>
              <dt>Nombre</dt><dd>{contact.name || '—'}</dd>
              <dt>Score</dt><dd>{contact.score ?? 0}</dd>
              <dt>Origen</dt><dd>{utmSource(contact)}</dd>
              <dt>Primera visita</dt><dd>{contact.first_seen ? formatDateTime(contact.first_seen) : '—'}</dd>
              <dt>Última visita</dt><dd>{contact.last_seen ? formatDateTime(contact.last_seen) : '—'}</dd>
            </dl>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Actividad</h3></div>
          <div className="card-body">
            {events.length === 0 ? (
              <div className="muted" style={{ fontSize: 13, padding: 8 }}>Sin eventos registrados.</div>
            ) : (
              <div className="col gap-2">
                {events.slice(0, 40).map((ev: any, i: number) => (
                  <div key={ev.id || i} className="row gap-2" style={{ fontSize: 12.5, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <Icon name="activity" size={12} />
                    <span style={{ flex: 1 }}>{ev.event_type || ev.event || 'evento'}{ev.data?.page_path ? ` · ${ev.data.page_path}` : ''}</span>
                    <span className="muted" style={{ fontSize: 11 }}>{ev.created_at ? relTime(ev.created_at) : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
